import * as React from 'react';
import * as PropTypes from 'prop-types';
import { classNames, getTabIndex } from '@progress/kendo-react-common';
import { useLocalization } from '@progress/kendo-react-intl';
import { dropZoneHint, dropZoneNote, messages } from './messages';
import { validatePackage } from '@progress/kendo-licensing';
import { packageMetadata } from './package-metadata';
/**
 * @hidden
 */
var TIME_TO_CHECK_DRAG = 100;
/**
 * Represents the ExternalDropZone component.
 */
export var ExternalDropZone = React.forwardRef(function (props, ref) {
    validatePackage(packageMetadata);
    var target = React.useRef(null);
    var externalDropZoneRef = React.useRef(null);
    var elementIntervalRef = React.useRef(null);
    var elementActiveRef = React.useRef(false);
    var lastElementDragOverRef = React.useRef(null);
    var _a = React.useState(false), overDropZone = _a[0], setOverDropZone = _a[1];
    var localization = useLocalization();
    var localizedHint = localization.toLanguageString(dropZoneHint, messages[dropZoneHint]);
    var localizedNote = localization.toLanguageString(dropZoneNote, messages[dropZoneNote]);
    var hintElement = props.customHint || React.createElement("span", null, localizedHint);
    var noteElement = props.customNote || React.createElement("span", null, localizedNote);
    var uploadRef = props.uploadRef;
    var focus = React.useCallback(function () {
        if (externalDropZoneRef.current) {
            externalDropZoneRef.current.focus();
        }
    }, [externalDropZoneRef]);
    React.useImperativeHandle(target, function () { return ({
        element: externalDropZoneRef.current,
        focus: focus,
        props: props
    }); });
    React.useImperativeHandle(ref, function () { return target.current; });
    var isDragOver = React.useCallback(function (prevDate) {
        return (new Date().getTime() - (prevDate || new Date()).getTime()) > TIME_TO_CHECK_DRAG;
    }, []);
    var handleOnDrop = React.useCallback(function (event) {
        event.preventDefault();
        var droppedFiles = event.dataTransfer.files;
        if (droppedFiles.length > 0 && !props.disabled && uploadRef && uploadRef.current) {
            if (uploadRef.current.onAdd) {
                event.preventDefault();
                uploadRef.current.onAdd(droppedFiles);
            }
        }
    }, [props.disabled, uploadRef]);
    var handleOnElementDragEnter = React.useCallback(function () {
        if (elementActiveRef) {
            elementActiveRef.current = true;
            var locElementInterval = function () {
                if (isDragOver(lastElementDragOverRef.current)) {
                    setOverDropZone(false);
                    elementActiveRef.current = false;
                    clearInterval(elementIntervalRef.current);
                    elementIntervalRef.current = null;
                    lastElementDragOverRef.current = null;
                }
            };
            elementIntervalRef.current = setInterval(locElementInterval, TIME_TO_CHECK_DRAG);
        }
    }, [elementActiveRef, elementIntervalRef, lastElementDragOverRef]);
    var handleOnElementDragOver = React.useCallback(function (event) {
        event.preventDefault();
        lastElementDragOverRef.current = new Date();
        setOverDropZone(true);
    }, [lastElementDragOverRef]);
    return (React.createElement("div", { ref: externalDropZoneRef, id: props.id, className: classNames('k-external-dropzone', {
            'k-external-dropzone-hover': overDropZone,
            'k-state-disabled': props.disabled
        }, props.className), tabIndex: getTabIndex(props.tabIndex, props.disabled, undefined), onDrop: handleOnDrop, onDragEnter: handleOnElementDragEnter, onDragOver: handleOnElementDragOver },
        React.createElement("div", { style: props.style, className: 'k-dropzone-inner' },
            React.createElement("span", { className: 'k-icon k-i-upload' }),
            React.createElement("span", { className: 'k-dropzone-hint' }, hintElement),
            React.createElement("span", { className: 'k-dropzone-note' }, noteElement))));
});
var propTypes = {
    id: PropTypes.string,
    tabIndex: PropTypes.number,
    editorRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any })
    ]),
    disabled: PropTypes.bool
};
ExternalDropZone.displayName = 'KendoReactExternalDropZone';
// TODO: delete casting when @types/react is updated!
ExternalDropZone.propTypes = propTypes;
//# sourceMappingURL=ExternalDropZone.js.map